
import { useState, useEffect, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Target, TrendingUp, Wallet, PiggyBank, Plus, Coins } from "lucide-react";
import { Button } from "@/components/ui/button";
import { GoalDialog } from "@/components/goals/GoalDialog";
import { GoalCard, SavingsGoal } from "@/components/goals/GoalCard";
import { GoalContributeDialog } from "@/components/goals/GoalContributeDialog";
import { toast } from "@/hooks/use-toast";
import { v4 as uuidv4 } from "uuid";

/**
 * The Save component manages user savings goals.
 * 
 * This page allows users to:
 * - View their existing savings goals
 * - Create new savings goals
 * - Edit existing goals
 * - Delete goals
 * - Contribute funds to goals
 */
const Save = () => {
  // State for goals
  const [goals, setGoals] = useState<SavingsGoal[]>(() => {
    // Initialize from localStorage if available
    const savedGoals = localStorage.getItem("savingsGoals");
    return savedGoals ? JSON.parse(savedGoals) : [];
  });

  // State for dialogs
  const [isAddGoalOpen, setIsAddGoalOpen] = useState(false);
  const [isContributeOpen, setIsContributeOpen] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState<SavingsGoal | null>(null);

  // Save goals to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem("savingsGoals", JSON.stringify(goals));
  }, [goals]);

  // Feature cards for the top of the page
  const savingFeatures = [
    {
      title: "Set Goals",
      description: "Define your savings targets and timelines",
      icon: Target,
    },
    {
      title: "Track Progress",
      description: "Monitor your savings growth over time",
      icon: TrendingUp,
    },
    {
      title: "Manage Budget",
      description: "Control your spending and optimize savings",
      icon: Wallet,
    },
  ];

  // Handlers for goal operations
  const handleAddGoal = useCallback((goalData: Omit<SavingsGoal, "id" | "currentAmount" | "createdAt">) => {
    // Create a new goal with required fields
    const newGoal: SavingsGoal = {
      id: uuidv4(),
      currentAmount: 0,
      createdAt: new Date(),
      ...goalData,
    };
    
    setGoals(prevGoals => [...prevGoals, newGoal]);
    
    toast({
      title: "Goal created",
      description: `Your "${newGoal.name}" goal has been created.`,
    });
  }, []);

  const handleEditGoal = useCallback((goalData: SavingsGoal) => {
    setGoals(prevGoals => 
      prevGoals.map(goal => 
        goal.id === goalData.id ? { ...goalData } : goal
      )
    );
    
    toast({
      title: "Goal updated",
      description: `Your "${goalData.name}" goal has been updated.`,
    });
  }, []);

  const handleDeleteGoal = useCallback((goalId: string) => {
    const goalToDelete = goals.find(goal => goal.id === goalId);
    
    if (!goalToDelete) return;
    
    setGoals(prevGoals => prevGoals.filter(goal => goal.id !== goalId));
    
    toast({
      title: "Goal deleted",
      description: `Your "${goalToDelete.name}" goal has been deleted.`,
    });
  }, [goals]);

  const handleOpenContribute = useCallback((goalId: string) => {
    const goal = goals.find(g => g.id === goalId);
    if (goal) {
      setSelectedGoal(goal);
      setIsContributeOpen(true);
    }
  }, [goals]);

  const handleContribute = useCallback((goalId: string, amount: number) => {
    setGoals(prevGoals => 
      prevGoals.map(goal => {
        if (goal.id === goalId) {
          const newAmount = goal.currentAmount + amount;
          const isComplete = newAmount >= goal.target;
          
          // Show different toast messages based on completion
          if (isComplete) {
            toast({
              title: "Goal achieved! 🎉",
              description: `Congratulations! You've fully funded your "${goal.name}" goal.`,
            });
          } else {
            toast({
              title: "Contribution added",
              description: `Added $${amount.toFixed(2)} to your "${goal.name}" goal.`,
            });
          }
          
          return {
            ...goal,
            currentAmount: Math.min(newAmount, goal.target),
          };
        }
        return goal;
      })
    );
  }, []);

  // Handler for editing an existing goal
  const handleOpenEdit = useCallback((goal: SavingsGoal) => {
    setSelectedGoal(goal);
    setIsAddGoalOpen(true);
  }, []);

  // Calculate total savings across all goals
  const totalSaved = goals.reduce((sum, goal) => sum + goal.currentAmount, 0);
  const totalTarget = goals.reduce((sum, goal) => sum + goal.target, 0);

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header section */}
      <div className="flex flex-col gap-2">
        <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
          Save
        </h1>
        <p className="text-gray-600">Manage your savings goals</p>
      </div>

      {/* Feature cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {savingFeatures.map((feature, index) => (
          <Card
            key={index}
            className="group border border-gray-100/50 shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer overflow-hidden hover:bg-white/80 backdrop-blur-sm"
          >
            <div className="p-6 relative">
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute inset-0 bg-gradient-to-br from-white/80 to-transparent" />
              </div>
              
              <div className="relative flex items-start gap-4">
                <div className="p-3 rounded-xl bg-accent/10 group-hover:scale-105 transition-transform duration-300">
                  <feature.icon className="w-6 h-6 text-accent" />
                </div>
                <div className="space-y-1">
                  <h3 className="font-semibold text-gray-800 group-hover:text-gray-900 transition-colors">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-gray-500 group-hover:text-gray-600 transition-colors">
                    {feature.description}
                  </p>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Summary card */}
      {goals.length > 0 && (
        <Card className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-none">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-primary/10">
                <PiggyBank className="w-8 h-8 text-primary" />
              </div>
              <div>
                <h3 className="text-xl font-semibold">Total Savings</h3>
                <p className="text-sm text-gray-600">Across all your goals</p>
              </div>
            </div>
            
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="text-center md:text-right">
                <p className="text-sm text-gray-600">Current</p>
                <p className="text-2xl font-bold">${totalSaved.toFixed(2)}</p>
              </div>
              
              <div className="text-center md:text-right">
                <p className="text-sm text-gray-600">Target</p>
                <p className="text-2xl font-bold">${totalTarget.toFixed(2)}</p>
              </div>
              
              <div className="text-center md:text-right">
                <p className="text-sm text-gray-600">Progress</p>
                <p className="text-2xl font-bold">
                  {totalTarget > 0 
                    ? Math.round((totalSaved / totalTarget) * 100)
                    : 0}%
                </p>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Goals section */}
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-semibold">Your Savings Goals</h2>
          <Button onClick={() => {
            setSelectedGoal(null);
            setIsAddGoalOpen(true);
          }}>
            <Plus className="w-4 h-4 mr-2" /> Add Goal
          </Button>
        </div>
        
        {/* Empty state */}
        {goals.length === 0 ? (
          <div className="text-center py-12 border-2 border-dashed rounded-xl bg-gray-50">
            <div className="flex justify-center mb-4">
              <div className="p-3 rounded-full bg-primary/10">
                <Coins className="w-8 h-8 text-primary" />
              </div>
            </div>
            <h3 className="text-xl font-medium mb-2">No savings goals yet</h3>
            <p className="text-gray-600 max-w-md mx-auto mb-6">
              Start by creating your first savings goal. Track your progress and reach your financial targets!
            </p>
            <Button onClick={() => {
              setSelectedGoal(null);
              setIsAddGoalOpen(true);
            }}>
              <Plus className="w-4 h-4 mr-2" /> Create Your First Goal
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {goals.map((goal) => (
              <GoalCard
                key={goal.id}
                goal={goal}
                onEdit={handleOpenEdit}
                onDelete={handleDeleteGoal}
                onContribute={handleOpenContribute}
              />
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Goal Dialog */}
      <GoalDialog
        open={isAddGoalOpen}
        onOpenChange={setIsAddGoalOpen}
        onSave={selectedGoal ? handleEditGoal : handleAddGoal}
        editGoal={selectedGoal}
      />

      {/* Contribute Dialog */}
      <GoalContributeDialog
        open={isContributeOpen}
        onOpenChange={setIsContributeOpen}
        goal={selectedGoal}
        onContribute={handleContribute}
      />
    </div>
  );
};

export default Save;
